import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { VersionHistory } from './VersionHistory';
import { 
    Sparkles, 
    Check, 
    Loader2,
    FileText,
    Route,
    MessageCircleQuestion,
    Copy,
    Wand2,
    Menu,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    X,
    Moon,
    Sun,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';

const AUTOSAVE_DELAY = 1000;
const AI_RATE_LIMIT_KEY = 'ideavault_ai_usage';
const AI_RATE_LIMIT_MAX = 10;
const AI_RATE_LIMIT_WINDOW = 60 * 60 * 1000;

const AI_TOOLS = [
    { 
        id: 'refine', 
        icon: Sparkles, 
        label: 'Refine', 
        description: 'Get perspectives on clarity and structure',
        color: 'text-indigo-500',
        bgColor: 'bg-indigo-500/10',
        borderColor: 'border-indigo-500/20'
    },
    { 
        id: 'roadmap', 
        icon: Route, 
        label: 'Roadmap', 
        description: 'Explore possible paths forward',
        color: 'text-emerald-500',
        bgColor: 'bg-emerald-500/10',
        borderColor: 'border-emerald-500/20'
    },
    { 
        id: 'critique', 
        icon: MessageCircleQuestion, 
        label: 'Challenge', 
        description: 'Surface blind spots and assumptions',
        color: 'text-amber-500',
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/20'
    },
];

const getAIUsage = () => {
    try {
        const data = JSON.parse(sessionStorage.getItem(AI_RATE_LIMIT_KEY) || '{}');
        const now = Date.now();
        if (data.resetAt && now > data.resetAt) {
            return { count: 0, resetAt: now + AI_RATE_LIMIT_WINDOW };
        }
        return { count: data.count || 0, resetAt: data.resetAt || now + AI_RATE_LIMIT_WINDOW };
    } catch {
        return { count: 0, resetAt: Date.now() + AI_RATE_LIMIT_WINDOW };
    }
};

const incrementAIUsage = () => {
    const usage = getAIUsage();
    usage.count += 1;
    sessionStorage.setItem(AI_RATE_LIMIT_KEY, JSON.stringify(usage));
    return usage;
};

const getRemainingCalls = () => {
    const usage = getAIUsage();
    return Math.max(0, AI_RATE_LIMIT_MAX - usage.count);
};

// Individual AI Tool Result Component
const AIToolResult = ({ tool, result, isLoading, error, isCollapsed, onToggleCollapse, onCopy, onInsert, onClear }) => {
    const [copied, setCopied] = useState(false);
    const Icon = tool.icon;

    const handleCopy = async () => {
        if (result) {
            await navigator.clipboard.writeText(result);
            setCopied(true);
            toast.success('Copied to clipboard');
            setTimeout(() => setCopied(false), 2000);
        }
    };

    if (!result && !isLoading && !error) return null;

    return (
        <div className={`rounded-xl border ${tool.borderColor} overflow-hidden transition-all duration-200`}>
            {/* Header - always visible */}
            <div 
                className={`px-4 py-3 ${tool.bgColor} flex items-center justify-between cursor-pointer`}
                onClick={onToggleCollapse}
            >
                <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${tool.color}`} />
                    <span className="text-sm font-medium text-foreground">{tool.label}</span>
                    {isLoading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                </div>
                <div className="flex items-center gap-1">
                    {result && !isCollapsed && (
                        <>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); onInsert(); }}
                                className="h-7 px-2 text-xs hover:bg-background/50"
                            >
                                Insert
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleCopy(); }}
                                className="h-7 px-2 text-xs hover:bg-background/50"
                            >
                                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            </Button>
                        </>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onClear(); }}
                        className="h-7 w-7 p-0 hover:bg-background/50"
                    >
                        <X className="w-3 h-3" />
                    </Button>
                    {isCollapsed ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                    )}
                </div>
            </div>
            
            {/* Content - collapsible */}
            {!isCollapsed && (
                <div className="p-4 bg-card/50">
                    {isLoading && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Thinking...
                        </div>
                    )}
                    {error && (
                        <p className="text-sm text-destructive">{error}</p>
                    )}
                    {result && (
                        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-foreground/90 leading-relaxed">
                            {result}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export const IdeaEditor = ({
    idea,
    onSave,
    isSaving,
    lastSaved,
    onAIAction,
    aiLoading,
    aiResults = {},
    aiErrors = {},
    onOpenMobileSidebar,
}) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showAITools, setShowAITools] = useState(false);
    const [customPrompts, setCustomPrompts] = useState({ refine: '', roadmap: '', critique: '' });
    const [showSavedIndicator, setShowSavedIndicator] = useState(false);
    const [rateLimitWarning, setRateLimitWarning] = useState(false);
    const [collapsedResults, setCollapsedResults] = useState({});
    const [loadingTools, setLoadingTools] = useState({});
    const [showHistory, setShowHistory] = useState(false);

    const { theme, toggleTheme } = useTheme();

    const saveTimeoutRef = useRef(null);
    const titleRef = useRef(null);
    const contentRef = useRef(null);
    
    useEffect(() => {
        if (idea) {
            setTitle(idea.title || '');
            setContent(idea.content || '');
            setHasUnsavedChanges(false);
            setShowAITools(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idea?.id]);
    
    const triggerSave = useCallback(() => {
        if (idea && (title !== idea.title || content !== idea.content)) {
            onSave(idea.id, { title, content });
            setHasUnsavedChanges(false);
            setShowSavedIndicator(true);
            setTimeout(() => setShowSavedIndicator(false), 2000);
        }
    }, [idea, title, content, onSave]);

    useEffect(() => {
        if (hasUnsavedChanges) {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = setTimeout(triggerSave, AUTOSAVE_DELAY);
        }
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        };
    }, [hasUnsavedChanges, triggerSave]);
    
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    useEffect(() => {
        if (showAITools) {
            const remaining = getRemainingCalls();
            setRateLimitWarning(remaining <= 2);
        }
    }, [showAITools]);
    
    const handleTitleChange = (e) => {
        setTitle(e.target.value);
        setHasUnsavedChanges(true);
    };
    
    const handleContentChange = (e) => {
        setContent(e.target.value);
        setHasUnsavedChanges(true);
    };
    
    const handleTitleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            contentRef.current?.focus();
        }
    };

    const handleRunAI = (toolId) => {
        if (!content.trim()) {
            toast.error('Write some content first');
            return;
        }
        
        const remaining = getRemainingCalls();
        if (remaining <= 0) {
            toast.error('Rate limit reached. Try again later.');
            return;
        }
        
        incrementAIUsage();
        setRateLimitWarning(remaining - 1 <= 2);
        setLoadingTools(prev => ({ ...prev, [toolId]: true }));
        // New results start in collapsed preview state
        setCollapsedResults(prev => ({ ...prev, [toolId]: true }));
        onAIAction(toolId, { title, content, customPrompt: customPrompts[toolId] });
    };

    // Update loading state when aiLoading changes
    useEffect(() => {
        if (!aiLoading) {
            setLoadingTools({});
        }
    }, [aiLoading]);

    const handleInsertResult = (toolId) => {
        const result = aiResults[toolId];
        if (result) {
            const newContent = content + '\n\n---\n\n' + result;
            setContent(newContent);
            setHasUnsavedChanges(true);
            toast.success('Inserted into your idea');
        }
    };

    const handleClearResult = (toolId) => {
        // This would need to be handled by parent - for now just collapse
        setCollapsedResults(prev => ({ ...prev, [toolId]: true }));
    };

    const toggleResultCollapse = (toolId) => {
        setCollapsedResults(prev => ({ ...prev, [toolId]: !prev[toolId] }));
    };
    
    const formatLastSaved = () => {
        if (!lastSaved) return null;
        const date = new Date(lastSaved);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    const hasAnyResults = Object.values(aiResults).some(r => r) || Object.values(aiErrors).some(e => e);
    
    // Empty state
    if (!idea) {
        return (
            <div 
                className="flex-1 flex flex-col items-center justify-center p-6 md:p-8 editor-gradient"
                data-testid="empty-editor-state"
            >
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onOpenMobileSidebar}
                    className="absolute top-4 left-4 h-11 w-11 rounded-full lg:hidden"
                    data-testid="mobile-menu-btn"
                >
                    <Menu className="h-5 w-5 text-muted-foreground" />
                </Button>
                
                <div className="max-w-md text-center animate-fade-in">
                    <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-6 md:mb-8 rounded-2xl bg-card shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] flex items-center justify-center">


                        <FileText className="w-8 h-8 md:w-10 md:h-10 text-muted-foreground/50" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-light text-foreground mb-3 md:mb-4">
                        Your private thinking space
                    </h2>
                    <p className="text-muted-foreground leading-relaxed text-base md:text-lg px-4">
                        Select an idea from the sidebar or create a new one. 
                        Everything stays in your browser.
                    </p>
                </div>
            </div>
        );
    }
    
    return (
        <div 
            className="flex-1 flex flex-col h-full overflow-hidden relative editor-gradient"
            data-testid="idea-editor"
        >
            {/* Editor Header */}
            <div className="flex items-center justify-between px-4 md:px-8 py-3 md:py-4 border-b border-border/60 bg-card/40 backdrop-blur-sm">
                <div className="flex items-center gap-2 md:gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onOpenMobileSidebar}
                        className="h-11 w-11 rounded-full lg:hidden mr-1"
                        data-testid="mobile-menu-btn"
                    >
                        <Menu className="h-5 w-5 text-muted-foreground" />
                    </Button>
                    
                    <div className={`flex items-center gap-2 text-sm text-muted-foreground transition-opacity duration-200`}>
                        {isSaving ? (
                            <>
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                <span className="hidden sm:inline">Saving...</span>
                            </>
                        ) : hasUnsavedChanges ? (
                            <>
                                <div className="h-2 w-2 rounded-full bg-amber-400" />
                                <span className="hidden sm:inline">Unsaved</span>
                            </>
                        ) : lastSaved ? (
                            <>
                                <Check className={`h-3.5 w-3.5 text-emerald-500 transition-transform duration-200 ${showSavedIndicator ? 'scale-110' : ''}`} />
                                <span className="hidden sm:inline">Saved {formatLastSaved()}</span>
                            </>
                        ) : null}
                    </div>
                </div>
                
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleTheme}
                        className="h-10 w-10 rounded-full hover:bg-accent"
                        aria-label="Toggle theme"
                    >
                        {theme === 'dark' ? (
                            <Sun className="h-4 w-4 text-amber-400" />
                        ) : (
                            <Moon className="h-4 w-4 text-muted-foreground" />
                        )}
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowHistory(!showHistory)}
                        className="rounded-full h-10 md:h-9 px-3 md:px-4 gap-1.5 md:gap-2 text-muted-foreground hover:text-foreground hover:bg-accent"
                    >
                        <FileText className="h-4 w-4" />
                        <span className="hidden sm:inline">History</span>
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAITools(!showAITools)}
                        className={`rounded-full h-10 md:h-9 px-3 md:px-4 gap-1.5 md:gap-2 transition-all duration-200 ${
                            showAITools 
                                ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                        }`}
                        data-testid="toggle-ai-tools-btn"
                    >
                        <Wand2 className="h-4 w-4" />
                        <span className="hidden sm:inline">AI Assist</span>
                    </Button>
                </div>
            </div>

            {/* AI Tools Section */}
            <div className={`overflow-hidden transition-all duration-200 ease-out ${
                showAITools ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
            }`}>
                <div className="px-4 md:px-8 py-4 md:py-5 bg-card/60 backdrop-blur-sm border-b border-border/60">
                    {/* Rate limit warning */}
                    {rateLimitWarning && (
                        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-700 dark:text-amber-400">
                                {getRemainingCalls() <= 0 
                                    ? 'Rate limit reached. Try again later.'
                                    : `${getRemainingCalls()} AI assists remaining this session.`
                                }
                            </p>
                        </div>
                    )}
                    
                    {/* All three AI tools - always visible */}
                    <div className="space-y-4">
                        {AI_TOOLS.map((tool) => {
                            const Icon = tool.icon;
                            const isThisLoading = loadingTools[tool.id];
                            const hasResult = aiResults[tool.id] || aiErrors[tool.id];
                            
                            return (
                                <div key={tool.id} className="space-y-2">
                                    {/* Tool header with run button */}
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3 flex-1">
                                            <div className={`p-2 rounded-lg ${tool.bgColor}`}>
                                                <Icon className={`w-4 h-4 ${tool.color}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-sm text-foreground">{tool.label}</div>
                                                <div className="text-xs text-muted-foreground">{tool.description}</div>
                                            </div>
                                        </div>
                                        <Button
                                            onClick={() => handleRunAI(tool.id)}
                                            disabled={isThisLoading || !content.trim() || getRemainingCalls() <= 0}
                                            size="sm"
                                            className={`rounded-full h-9 px-4 ${tool.bgColor} ${tool.color} border ${tool.borderColor} hover:opacity-80`}
                                            variant="ghost"
                                        >
                                            {isThisLoading ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                'Run'
                                            )}
                                        </Button>
                                    </div>
                                    
                                    {/* Custom prompt for refine */}
                                    {tool.id === 'refine' && (
                                        <input
                                            type="text"
                                            value={customPrompts.refine}
                                            onChange={(e) => setCustomPrompts(prev => ({ ...prev, refine: e.target.value }))}
                                            placeholder="Optional: focus on something specific..."
                                            className="w-full px-4 py-2.5 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring/20 placeholder:text-muted-foreground"
                                        />
                                    )}
                                    
                                    {/* Result for this tool */}
                                    {hasResult && (
                                        <AIToolResult
                                            tool={tool}
                                            result={aiResults[tool.id]}
                                            isLoading={isThisLoading}
                                            error={aiErrors[tool.id]}
                                            isCollapsed={collapsedResults[tool.id] ?? true}
                                            onToggleCollapse={() => toggleResultCollapse(tool.id)}
                                            onInsert={() => handleInsertResult(tool.id)}
                                            onClear={() => handleClearResult(tool.id)}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-4 text-center">
                        Using managed AI. These are perspectives to consider, not advice.
                    </p>
                </div>
            </div>
            
            {/* Editor Content */}
            <ScrollArea className="flex-1">
                <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
                    <input
                        ref={titleRef}
                        type="text"
                        value={title}
                        onChange={handleTitleChange}
                        onKeyDown={handleTitleKeyDown}
                        placeholder="Title your idea..."
                        className="w-full bg-transparent border-none outline-none text-2xl md:text-4xl font-light tracking-tight text-foreground placeholder:text-muted-foreground/40 mb-6 md:mb-8"
                        data-testid="idea-title-input"
                    />
                    
                    <Textarea
                        ref={contentRef}
                        value={content}
                        onChange={handleContentChange}
                        placeholder="Start writing your idea...

Let your thoughts flow freely. This is your private space to think, explore, and develop your ideas.

What's on your mind?"
                        className="w-full min-h-[50vh] md:min-h-[60vh] bg-transparent border-none outline-none resize-none editor-content text-lg md:text-xl leading-relaxed text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 focus-visible:ring-offset-0 p-0"
                        data-testid="idea-content-input"
                    />
                    {showHistory && idea && (
                        <div className="hidden lg:block">
                            <VersionHistory
                                ideaId={idea.id}
                                onRestore={(version) => {
                                    setTitle(version.title || '');
                                    setContent(version.content || '');
                                    setHasUnsavedChanges(true);
                                }}
                            />
                        </div>
                    )}

                </div>
            </ScrollArea>
        </div>
    );
};
